<template>
    <div class="customer-facing">
        <div class="ui" :class="componentClass">
            <transition name="fade" appear mode="out-in">
                <div class="child" v-if="!ready">
                    <loading-icon color="white" :size="100"></loading-icon>
                </div>

                <template v-else>
                    <component v-bind:is="displayComponent()" class="child" :err="errType"/>
                </template>

            </transition>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import {mapGetters} from 'vuex';
import LoadingIcon from '../../components/LoadingIcon';
import OccupancyAvailable from '../../components/CustomerFacing/OccupancyAvailable';
import Error from '../../components/CustomerFacing/Error';
import OccupancyFull from '../../components/CustomerFacing/OccupancyFull';
import Closed from '../../components/CustomerFacing/Closed';

export default {
  name: 'CustomerFacing',
  components: {Closed, OccupancyFull, Error, OccupancyAvailable, LoadingIcon},
  data() {
    return {
      limitInterval: null,
      dataInterval: null,
      ewtInterval: null,
      languageInterval: null,
      isOpen: false,
      ready: false,
      errorCounter: 0,
      camErrors: undefined,
      state: undefined,
      statusTimer: undefined
    };
  },
  computed: {
    ...mapGetters(['storeLimit', 'storeOccupancy', 'interval']),
    atCapacity() {
      // bool if store is at capacity
      if (this.storeLimit && this.storeOccupancy) {
        return this.storeLimit.limit - this.storeOccupancy.sum <= 0 ? true : false;
      } else {
        return false;
      }
    },
    storeId() {
      return this.$route.params.id;
    },
    sum() {
      return this.storeOccupancy.sum < 0 ? 0 : this.storeOccupancy.sum;
    },

    componentClass() {
      if (!this.ready) {
        return 'loading';
      } else {
        switch (this.displayComponent()) {
          case Error:
            return 'unavailable';
          case Closed:
            return 'closed';
          case OccupancyFull:
            return 'atCapacity';
        }
        return 'default';
      }
    },

    errType() {
      let type = undefined;

      if (this.camErrors) {
        // Hardware error
        type = this.camErrors;
      } else if (this.errorCounter > 0) {
        // Data error
        type = 102;
      }

      return type;
    }
  },

  beforeDestroy() {
    window.clearInterval(this.dataInterval);
    window.clearInterval(this.limitInterval);
    window.clearInterval(this.languageInterval);
    window.clearInterval(this.ewtInterval);

    this.$store.dispatch('hours/CLEAR');
    this.$store.dispatch('CLEAR');

    window.clearTimeout(this.statusTimer);
  },


  watch: {
    interval() {
      window.clearInterval(this.dataInterval);
      this.dataInterval = window.setInterval(this.getStoreData, this.interval);
    }
  },

  mounted() {
    // Ensure store is set, used for refresh.
    this.$store.dispatch('SELECT_SITE', this.storeId);
    this.$store.dispatch('hours/GET_HOURS', this.storeId);
    this.$store.dispatch('hours/GET_OPEN', this.storeId);

    this.getStoreLimit();
    setTimeout(() => {
      this.limitInterval = window.setInterval(this.getStoreLimit, 5000);
      this.getStoreData();
      this.dataInterval = window.setInterval(this.getStoreData, this.interval);
      this.getEwt();
      this.ewtInterval = window.setInterval(this.getEwt, 60000);

    }, 500);

    // Ensure correct locale, default to 'en'
    this.$i18n.locale = this.$store.getters['i18n/current'] || 'en';
    if (this.$store.getters['i18n/selected'].length > 1) {
      this.rotateLanguages();
    }


    this.checkStatus();
  },

  methods: {
    displayComponent() {

      if (this.camErrors) {
        this.state = undefined;
        return Error;
      } else if (this.errorCounter >= 5) {
        this.state = undefined;
        return Error;
      }

      if (!this.isOpen) {
        this.state = undefined;
        return Closed;
      } else {
        return this.atCapacity ? OccupancyFull : OccupancyAvailable;
      }
    },

    isStoreOpen() {
      this.isOpen = this.$store.getters['hours/isOpen'](moment());

      if (!this.isOpen) {
        this.$store.dispatch('hours/GET_HOURS', this.storeId);
      }

      this.$store.dispatch('hours/DETERMINE_INTERVAL');
    },

    getEwt() {
      // NEW ENDPOINT
      axios.get(`https://occupancy-eu.walkbase.com/v1/intellicount/report/latest?site_id=${this.storeId}&api_key=${this.$store.getters.key}&forecast=true`)
        .then((res) => {
          this.$store.dispatch('SET_FORECASTS', [res.data.forecasts]);
        });
    },


    async getStoreLimit() {
      if (this.storeId) {
        await axios.get(`${process.env.VUE_APP_API_BASE}${process.env.VUE_APP_ENDPOINT_STORE_LIMIT}?api_key=${this.$store.getters.key}&format=json&site_id=${this.storeId}`)
          .then((res) => {
            this.$store.commit('setStoreLimit', res.data.results);
          });
      }
    },

    async getStoreData() {
      // get store occupancy, in, out, and sum count
      if (this.storeId) {
        await axios.get(`${process.env.VUE_APP_API_BASE}/b/v1/intellicount/report/latest/?site_id=${this.storeId}&api_key=${this.$store.getters.key}`)
          .then((res) => {
            if (res.status === 200) {
              this.$store.commit('setStoreOccupancy', res.data.count);
              if (!this.isOpen) {
                this.$store.dispatch('hours/GET_OPEN', this.storeId);
              }
              // Reset error counter on success

            } else if (res.status === 204) {
              this.$store.dispatch('hours/GET_OPEN', this.storeId);
            }

              if (this.errorCounter !== 0) this.errorCounter = 0;
          })
          .catch(() => {
            if (this.isOpen) {
              this.errorCounter++;
            }
          });

        if (!this.ready) {
          this.ready = true;
        }
      }
      this.isStoreOpen();
    },

    rotateLanguages() {
      this.languageInterval = setInterval(() => {
        this.$store.dispatch('i18n/NEXT', this.$i18n);
      }, 10000);
    },

    checkStatus() {
      window.clearTimeout(this.statusTimer);

      axios.get(process.env.VUE_APP_API_BASE + `/a/occupancy/status/${this.storeId}/?api_key=${this.$store.getters.key}`)
        .then((res) => {
          if (res.data.people_counters) {
            const now = moment();

            // Default to 10 minutes if ENV var isn't present.
            let threshold = process.env.VUE_APP_CAM_RESPONSE_THRESHOLD ? process.env.VUE_APP_CAM_RESPONSE_THRESHOLD : 600000;
            let unresponsive_cams = res.data.people_counters.filter((pc) => {
              return now.diff(moment(pc.last_interaction)) > threshold;
            });

            if (unresponsive_cams.length > 0) {
              // A camera hasn't reported in in at least 10 minutes. INVESTIGATE
              this.camErrors = 101; // Code used to indicate ONE or MORE of the cameras haven't updated in X time

            } else {
              if (this.camErrors) {
                this.camErrors = undefined;
              }
            }
          } else {
            // THROW ERROR. THERE SHOULD BE SOME
            this.camErrors = 100; // Code used to indicate there SHOULD be an active camera.
          }

        })
        .then(() => {
          this.statusTimer = setTimeout(() => {
            this.checkStatus();
          }, 60000);
        });
    }
  }
};

</script>

<style lang="scss">

.customer-facing {
    background-color: $brand-gray;
    color: $brand-white;
    text-align: center;
    position: relative;
    width: inherit;
    height: inherit;
}

.ui {
    width: 100%;
    height: 100%;
    position: relative;
    background-color: $brand-green;
    transition: background-color cubic-bezier(0.55, 0, 0.1, 1) 300ms;

    .child {
        display: flex;
        flex-flow: column;
        align-content: center;
        justify-content: space-evenly;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        padding: 4rem;
    }

    &.unavailable {
        background-color: #FFF;
        text-align: center;
        color: $brand-black;
    }

    &.atCapacity {
        background-color: $brand-warning;
    }

    &.closed {
        background-color: #89cff0;
    }

    .title {
        font-weight: 700;
        font-size: 3.5em;
    }

    .status-icon {
        height: 200px;
        margin: 2rem;
    }

    .action {
        font-weight: 700;
        font-size: 3em;
    }

    .instructions {
        font-weight: 600;
        font-size: 2em;
        line-height: 1.3em;
    }

    .estimated-time {
        width: 745px;
        height: 240px;
        background-color: $brand-white;
        margin: 0 auto;

        font-size: 57px;
        font-weight: 700;
        line-height: 1.3em;
        color: $brand-warning;
        text-align: center;

        display: flex;
        align-items: center;
        justify-content: center;
    }

    .capacity {
        font-weight: 500;
        font-size: 3em;
    }
}

@media only screen and (max-width: 414px) {
    .ui {
        padding: 1rem;

        .title {
            font-size: 3em;
        }

        .action {
            font-size: 2em;
        }

        .instructions {
            font-size: 1.5em;
        }

        .capacity {
            font-size: 2em;
        }

        .status-icon {
            height: 120px;
        }
    }
}

@media only screen and (min-width: 1024px) {
    .ui {
        .status-icon {
            height: 360px;
        }
    }
}

@media only screen and (min-width: 1024px) and (max-height: 768px) {
    .ui {
        padding: 2rem;

        .status-icon {
            height: 200px;
        }
    }
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .3s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
